import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import {
  PerformanceContext,
  PerformanceProvider,
} from "./context/PerformanceProvider";
import useGetPerformanceChart from "./hook/useGetPerformanceChart";
import ReferrerTable from "./ReferrerTable";
import LineChart from "./LineChart";
import { Box, Grid } from "@mui/material";
import DonutChart from "./component/DonutChart";
import BarChart from "./component/BarChart";
import MapChart from "./component/MapChart";
import { useSelector } from "react-redux";
import {
  selectCDPModule,
  selectEcosystemName,
} from "features/metadesk/redux/entityConfig/entityConfig.selector";
import axios from "axios";
import { useOutletContext, useParams } from "react-router";
import { apiUrl } from "features/configure";
import useAnalyticsModule from "hooks/useAnalyticsModule";
import useNavigateEntity from "hooks/useNavigateEntity";
import { useIntl } from "react-intl";
import CreateAudiencePopover from "./component/CreateAudiencePopover";
import useGetPagePermission from "hooks/useGetPagePermission";
import Button, { SIZE } from "@metacrm/metacrm-material-ui/dist/Button";
import moment from "moment";
import EmptyImgStatus from "components/EmptyState/EmptyImageStatus";
import CustomButtonDropdown from "components/CustomButtonDropdown/CustomButtonDropdown";
import CDPExportPopover from "./component/CDPExportPopover";
import DatePicker from "@metacrm/metacrm-material-ui/dist/DatePicker";
import LoadingFilter from "components/LoadingComponent/LoadingFilter";

export default function TrafficSource() {
  return (
    <PerformanceProvider>
      <Charts />
    </PerformanceProvider>
  );
}

const Charts = () => {
  const datePickerRef = useRef(null);
  const { os, device, browser, referrers, performance, country } =
    useGetPerformanceChart();
  const { dateRange, setDateRange, isLoading } = useContext(PerformanceContext);
  const [entityName] = useOutletContext();
  const [objectiveData, setObjectiveData] = useState();
  const { navigateEntity } = useNavigateEntity();
  const { formatMessage } = useIntl();
  const ecosystemName = useSelector(selectEcosystemName);
  const hasCDP = useSelector(selectCDPModule);
  const isShowObjective = ecosystemName !== "Aptos";
  const { attributionConnect, marketingModule } = useAnalyticsModule();
  const [openAudience, setOpenAudience] = useState(false);
  const { subEntityName } = useParams();

  const isConnected = subEntityName || attributionConnect;
  const [openExportCdp, setOpenExportCdp] = useState(false);

  const dynamicApiUrl = useMemo(() => {
    switch (true) {
      case !!subEntityName:
        return `apiEvaluate`;
      default:
        return `api`;
    }
  }, [subEntityName]);

  const dynamicEntityName = useMemo(() => {
    switch (true) {
      case !!subEntityName:
        return `${entityName}/${subEntityName}`;
      default:
        return entityName;
    }
  }, [entityName, subEntityName]);

  useEffect(() => {
    if (!isShowObjective) return;
    async function getTrackingObjective() {
      try {
        const result = await axios.get(
          apiUrl + `/${dynamicApiUrl}/trackingObjective/${dynamicEntityName}`
        );

        setObjectiveData(result.data);
      } catch (err) {}
    }
    getTrackingObjective();
  }, [isShowObjective]);

  const mapping = useMemo(() => {
    const createReducer = (keyField) => (acc, cur) => ({
      ...acc,
      [cur[keyField]]: cur,
      totalUsers: acc.totalUsers + cur.totalUser,
      totalWallets: acc.totalWallets + cur.walletsConnected,
      ...(isShowObjective && {
        objectiveAchieved: acc.objectiveAchieved + (cur.objectiveAchieved || 0),
      }),
    });

    const initialValue = {
      totalUsers: 0,
      totalWallets: 0,
      ...(isShowObjective && { objectiveAchieved: 0 }),
    };

    return {
      os: os?.reduce(createReducer("os"), initialValue),
      device: device?.reduce(createReducer("device"), initialValue),
      browser: browser?.reduce(createReducer("browser"), initialValue),
      total: performance?.reduce(
        (acc, cur) => ({
          users: acc.users + cur.totalUser,
          wallets: acc.wallets + cur.walletsConnected,
          ...(isShowObjective && {
            objectiveAchieved:
              acc.objectiveAchieved + (cur.objectiveAchieved || 0),
          }),
        }),
        {
          users: 0,
          wallets: 0,
          ...(isShowObjective && { objectiveAchieved: 0 }),
        }
      ),
    };
  }, [os, device, browser, performance, isShowObjective]);

  const pagePermission = useGetPagePermission();
  const { canEdit } = pagePermission("websiteTraffic");

  const renderActions = () => {
    if (!canEdit || subEntityName) return;
    const options = [];
    if (marketingModule && attributionConnect) {
      options.push({
        label: formatMessage({
          id: `global.btn.createAudience`,
        }),
        action: () => {
          setOpenAudience(true);
        },
      });
    }
    // // carve out from CDP 3 phase 1
    // if (hasCDP) {
    //   options.push({
    //     label: formatMessage({
    //       id: "websiteTraffic.dropdown.exportWalletsToCDP",
    //     }),
    //     action: () => {
    //       setOpenExportCdp(true);
    //     },
    //   });
    // }
    if (options.length == 0) return;
    return <CustomButtonDropdown options={options} />;
  };
  if (!isConnected) {
    return (
      <Box mt="12px" className="fade-up-more">
        <EmptyImgStatus
          type="tracking"
          backgroundImg="Traffic_Source"
          title={formatMessage({
            id: "emptyState.trackingTagNotInstalled.title",
          })}
          description={formatMessage({
            id: "emptyState.trackingTagNotInstalled.description",
          })}
          onClick={() =>
            navigateEntity(
              "/settings/general/integration/attribution?from=analytics/website"
            )
          }
        />
      </Box>
    );
  }
  const renderContent = () => {
    return (
      <Box className="fade-up-more">
        <ReferrerTable
          referrers={referrers}
          total={mapping.total}
          objectiveData={objectiveData}
          isShowObjective={isShowObjective}
          setOpenAudience={setOpenAudience}
          setOpenExportCdp={setOpenExportCdp}
        />
        <Grid spacing={1} container sx={{ mt: "24px" }} alignItems="stretch">
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <LineChart
                performance={performance}
                isShowObjective={isShowObjective}
                isLoading={isLoading.chart}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={3}>
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <DonutChart
                title={formatMessage({
                  id: "websiteTraffic.chart.activeUserByOs",
                })}
                data={os.map((o) => ({ name: o.os, value: o.totalUser }))}
                tooltip={function (params) {
                  const { name, value } = params;
                  return `${name}<br/>Users ${
                    mapping.os[name].totalUser
                  }<br/>Wallets ${
                    mapping.os[name].walletsConnected
                  }<br/>Objective Achieved ${
                    mapping.os[name].objectiveAchieved || "-"
                  }`;
                }}
                isLoading={isLoading.chart}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={3}>
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <DonutChart
                title={formatMessage({
                  id: "websiteTraffic.chart.activeUserByDevice",
                })}
                data={device.map((d) => ({
                  name: d.device,
                  value: d.totalUser,
                }))}
                tooltip={function (params) {
                  const { name, value } = params;
                  return `${name}<br/>Users ${
                    mapping.device[name].totalUser
                  }<br/>Wallets ${
                    mapping.device[name].walletsConnected
                  }<br/>Objective Achieved ${
                    mapping.device[name].objectiveAchieved || "-"
                  }`;
                }}
                isLoading={isLoading.chart}
              />
            </Box>
          </Grid>
          <Grid item xs={12} md={9} sx={{ height: "100%" }}>
            <MapChart
              country={country}
              isShowObjective={isShowObjective}
              isLoading={isLoading.chart}
            />
          </Grid>
          <Grid item xs={12} md={3} sx={{ height: "100%" }}>
            <BarChart
              isShowObjective={isShowObjective}
              title={formatMessage({
                id: "websiteTraffic.chart.activeUserByBrowser",
              })}
              data={browser}
              isLoading={isLoading.chart}
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <div className="fade-up-more">
      <CreateAudiencePopover
        objectiveData={objectiveData}
        onClose={() => {
          setOpenAudience(false);
        }}
        open={openAudience}
      />
      {openExportCdp && (
        <CDPExportPopover
          onClose={() => {
            setOpenExportCdp(false);
          }}
          metaData={openExportCdp}
        />
      )}
      <Box sx={{ position: "relative" }}>
        {(isLoading.table || isLoading.chart) && <LoadingFilter />}
        <Box
          sx={{
            alignItems: "center",
            bgcolor: "#fff",
            mt: 2,
            mb: "8px",
            p: "8px 12px",
            borderRadius: "4px",
          }}
          className="betweenRow"
        >
          <Box />
          <Box className="endRow">
            <DatePicker
              ref={datePickerRef}
              datePickerProps={{
                minDate: moment()
                  .utc()
                  .startOf("day")
                  .subtract(89, "day")
                  .toDate(),
              }}
              setDateRange={setDateRange}
              dateRange={dateRange}
              inputContainerStyle={{ height: "32px" }}
              quickSelectOptions={[
                {
                  days: 1,
                  label: formatMessage({ id: "datePicker.range.day" }),
                },
                {
                  days: 7,
                  label: formatMessage({ id: "datePicker.range.week" }),
                },
                {
                  days: 30,
                  label: formatMessage({ id: "datePicker.range.month" }),
                },
                {
                  days: "all",
                  label: formatMessage({ id: "datePicker.range.all" }),
                },
                {
                  days: "custom",
                  label: formatMessage({ id: "datePicker.range.custom" }),
                },
              ]}
            />
            {renderActions()}
          </Box>
        </Box>
        {renderContent()}
      </Box>
    </div>
  );
};
